import React from 'react';
import { Modal, View, Text, ScrollView, Pressable, TextStyle } from 'react-native';
import styles from './ModalDetailsDotation.style';
import { DotationEsag } from '../../api/Dotation/types';
import dayjs from 'dayjs';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { DROITS_CONFIG } from '../../modules/Droits/Droits.component';

type ModalDetailsDotationProps = {
  visible: boolean;
  data: DotationEsag;
  title: string;
  onClose: () => void;
};

export default function DetailsModal({ visible, data, title, onClose }: ModalDetailsDotationProps) {
  if (!data) return null;

  const config = DROITS_CONFIG[title as keyof typeof DROITS_CONFIG];
  if (!config) return null;

  const hasValidReliquat = !!data.complet?.dateReliquat && dayjs(data.complet.dateReliquat).isValid() && Number(data.complet?.droitReliquat.replace(',','.')) !== 0
  const hasN1 = (Number(String(data.complet?.droitN1 ?? '0').replace(',', '.')) || 0) !== 0
  const styleDroitEnCours = [styles.tableCell, {flex: data.complet?.droitHorsPeriode !== undefined ? 3 : 1}]
  const reliquatPasse = hasValidReliquat && dayjs().isAfter(data.complet?.dateReliquat, 'day') && (Number(data.complet?.droitReliquat.replace(',','.')) || 0) !== 0
  const complementReliquatPasse = reliquatPasse
    ? {textDecorationLine:  'line-through', textDecorationColor: "#000000"} as TextStyle
    : {};
  const styleResteReliquat = [styles.cellValue, complementReliquatPasse]
  const styleTotal = [styles.cellValue, {fontWeight: '900'} as TextStyle]

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          {/* En-tête du modal */}
          <View style={styles.modalHeader}>
            <MaterialCommunityIcons 
              name={config.icon}
              size={30}
              color={data.natureAbsence.couleurHexa}
            />
            <Text style={[styles.modalTitle, { color: data.natureAbsence.couleurHexa }]}>
              Détails {config.name}
            </Text>
          </View>

          <ScrollView style={styles.detailsTable}>
            {/* En-tête du tableau */}
            <View style={[styles.tableRow]}>
              <View style={styles.tableCellTitle}>
                <Text style={styles.headerText}> </Text>
              </View>
              {hasValidReliquat && (
                <View style={styles.tableCell}>
                  <Text style={styles.headerText}>N-1 *</Text>
                </View>
              )}
              <View style={styleDroitEnCours}>
                <Text style={styles.headerText}>En cours</Text>
                {data.complet?.droitHorsPeriode !== undefined && (
                  <>
                    <Text style={styles.headerText}>+ Hors Période</Text>
                    <Text style={styles.headerText}>+ Ancienneté</Text>
                  </>
                )}
              </View>
              <View style={styles.tableCell}>
                <Text style={styles.headerText}>Total</Text>
              </View>
              {hasN1 && (
                <View style={styles.tableCell}>
                  <Text style={styles.headerText}>N+1</Text>
                </View>
              )}
            </View>

            {/* Ligne Droit */}
            <View style={styles.tableRow}>
              <View style={styles.tableCellTitle}>
                <Text style={styles.cellText}>Droit</Text>
              </View>
              {hasValidReliquat && (
                <View style={styles.tableCell}>
                  <Text style={styleResteReliquat}>{data.complet?.droitReliquat || 0}</Text>
                  {reliquatPasse && <Text style={styles.cellValue}>{data.complet?.consommeReliquat || 0}</Text>}
                </View>
              )}
              <View style={styleDroitEnCours}>
                <Text style={styles.cellValue}>{data.complet?.droitPeriode || 0}</Text>
                {data.complet?.droitHorsPeriode !== undefined && (
                  <>
                    <Text style={styles.cellValue}>+ {data.complet.droitHorsPeriode || 0}</Text>
                    <Text style={styles.cellValue}>+ {data.complet?.droitAnciennete || 0}</Text>
                    <Text style={styles.cellValue}>= {data.complet?.droitEnCours || 0}</Text>
                  </>
                )}
              </View>
              <View style={styles.tableCell}>
                <Text style={styles.cellValue}>{data.complet?.droitTotal || 0}</Text>
              </View>
              {hasN1 && (
                <View style={styles.tableCell}>
                  <Text style={styles.cellValue}>{data.complet?.droitN1 || 0}</Text>
                </View>
              )}
            </View>

            {/* Ligne Pris */}
            <View style={styles.tableRow}>
              <View style={styles.tableCellTitle}>
                <Text style={styles.cellText}>Pris</Text>
              </View>
              {hasValidReliquat && (
                <View style={styles.tableCell}>
                  <Text style={styles.cellValue}>{data.complet?.prisReliquat || 0}</Text>
                </View>
              )}
              <View style={styleDroitEnCours}>
                <Text style={styles.cellValue}>{data.complet?.prisEnCours || 0}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text style={styles.cellValue}>{data.complet?.prisTotal || 0}</Text>
              </View>
              {hasN1 && (
                <View style={styles.tableCell}>
                  <Text style={styles.cellValue}>{data.complet?.prisN1 || 0}</Text>
                </View>
              )}
            </View>

            {/* Ligne CET si elle existe */}
            {data.complet?.CETReliquat !== undefined && (
              <View style={styles.tableRow}>
                <View style={styles.tableCellTitle}>
                  <Text style={styles.cellText}>CET</Text>
                </View>
                {hasValidReliquat && (
                  <View style={styles.tableCell}>
                    <Text style={styles.cellValue}>{data.complet.CETReliquat || 0}</Text>
                  </View>
                )}
                <View style={styleDroitEnCours}>
                  <Text style={styles.cellValue}>{data.complet.CETEnCours || 0}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text style={styles.cellValue}>{data.complet.CETTotal || 0}</Text>
                </View>
                {hasN1 && (
                  <View style={styles.tableCell}>
                    <Text style={styles.cellValue}> </Text>
                  </View>
                )}
              </View>
            )}

            {/* Ligne Reste */}
            <View style={styles.tableRow}>
              <View style={styles.tableCellTitle}>
                <Text style={styles.cellText}>Reste</Text>
              </View>
              {hasValidReliquat && (
                <View style={styles.tableCell}>
                  <Text style={styles.cellValue}>{data.complet?.resteReliquat || 0}</Text>
                </View>
              )}
              <View style={styleDroitEnCours}>
                <Text style={styles.cellValue}>{data.complet?.resteEnCours || 0}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text style={styleTotal}>{data.complet?.resteTotal || 0}</Text>
              </View>
              {hasN1 && (
                <View style={styles.tableCell}>
                  <Text style={styles.cellValue}>{data.complet?.resteN1 || 0}</Text>
                </View>
              )}
            </View>
          </ScrollView>
          {hasValidReliquat && (
            <Text>* Reliquats perdus le {dayjs(data.complet?.dateReliquat).format('DD/MM/YYYY')}</Text>
          )}

          {/* Bouton fermer */}
          <Pressable
            style={styles.closeButton}
            onPress={onClose}
            testID='BT_CLOSE'
          >
            <Text style={styles.closeButtonText}>Fermer</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
};